// Inspired by Chatbot-UI and modified to fit the needs of this project
// @see https://github.com/mckaywrigley/chatbot-ui/blob/main/components/Chat/ChatMessage.tsx

import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

import {
  ConfiguredReactMarkdown,
  SearchQueryProvider,
} from "@/components/AI/chat/Markdown";
import { cn } from "@/lib/utils";
import { useIsChatLoading } from "@/store/zustand/aiContextStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { Message, useSupabaseStore } from "@/store/zustand/supabaseStore";
import React, { useContext, useEffect, useMemo, useState } from "react";
import ContextButton from "../components/ContextButton";
import { EngSpecBlock } from "./messageBlocks/EngSpecBlock";
import { PRDBlock } from "./messageBlocks/PRDBlock";
import { ProgressBlock } from "./messageBlocks/ProgressBlock";
import { FigmaBlock } from "./messageBlocks/FigmaBlock";
import { ErrorsBlock } from "./messageBlocks/ErrorsBlock";
import { useImageUploader } from "@/components/AppHeader/FileUpload";
import { EnvVariablesInputBlock } from "./messageBlocks/EnvVariablesInputBlock";
import { ReasoningChatMessage } from "./messageBlocks/ReasoningBlock";
import { SearchGroundingSourcesChatMessage } from "./messageBlocks/SearchGroundingSourcesBlock";
import { VercelDeploymentBlock } from "./messageBlocks/VercelDeploymentBlock";
import { ToolCallBlock } from "./messageBlocks/ToolCallBlock";
import { parseMessageContentBlocks } from "./utils/messages";
import { ChatMessageUsageIndicator } from "./ChatMessageUsageIndicator";
import { MessageRestoreButton } from "./MessageRestoreButton";
import { MessageRestoreStateContext } from "./contexts/MessageRestoreStateContext";
import { usePendingQuestions } from "./contexts/PendingQuestionsContext";
import { useToolResults } from "./contexts/ToolResultsContext";

export interface ChatMessageProps {
  message: Message;
  isLastMessage: boolean;
  searchQuery?: string;
  isGreyedOut?: boolean;
}

export const ChatMessage = React.memo(
  ({
    message,
    isLastMessage,
    searchQuery,
    isGreyedOut = false,
  }: ChatMessageProps) => {
    // Check if this message contains the search query
    const hasSearchMatch = useMemo(() => {
      if (!searchQuery?.trim() || !message?.content) return false;
      return message.content.toLowerCase().includes(searchQuery.toLowerCase());
    }, [message?.content, searchQuery]);

    const fileToContentsFromApi = useCodebaseStore(
      (state) => state.fileToContents,
    );

    const isChatLoading = useIsChatLoading();

    const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

    // Get tool results from context to associate with tool calls
    const { getAllResults } = useToolResults();
    const toolResultsMap = getAllResults();

    const contentArray = useMemo(() => {
      return parseMessageContentBlocks(message, toolResultsMap);
    }, [message, toolResultsMap]);

    // Check if message contains dev-errors block
    const hasDevErrors = useMemo(() => {
      return contentArray.some((block) => block.type === "dev-errors");
    }, [contentArray]);

    const [updatedMessage, setUpdatedMessage] = useState(false);

    const { FilePreviewItem } = useImageUploader();

    const messageRestoreContext = useContext(MessageRestoreStateContext);
    if (!messageRestoreContext) {
      throw new Error(
        "ChatMessage must be used within MessageRestoreStateProvider",
      );
    }
    const { getRestoreState } = messageRestoreContext;

    // Get pending questions context for askUserQuestionsTool
    const { pendingQuestions, submitAnswers, isSubmittingAnswers } =
      usePendingQuestions();

    /**
     * If the message has code changes, also store the current code contents
     * so that if they change it no longer allows it to be applied
     */
    useEffect(() => {
      try {
        // AI hasn't finished streaming
        if (!supabaseClient || !message.message_context) {
          return;
        }

        if (updatedMessage || message.role !== "assistant") {
          return;
        }

        let needToChange = false;
        const filesToReload: string[] = [];
        const newContents = contentArray
          .map((item, index) => {
            if (
              item.type === "aiCodeChange" &&
              fileToContentsFromApi[item.aiCodeChange.filename] === undefined
            ) {
              filesToReload.push(item.aiCodeChange.filename);
              return "";
            }

            if (
              item.type === "aiCodeChange" &&
              item.aiCodeChange.edit &&
              !item.aiCodeChange.edit.oldContents
            ) {
              needToChange = true;

              const { startLine, endLine } = item.aiCodeChange.edit;

              const fileContents =
                fileToContentsFromApi[item.aiCodeChange.filename];

              item.aiCodeChange.edit["oldContents"] = fileContents
                ?.split("\n")
                .slice(
                  startLine !== undefined ? startLine - 1 : 0,
                  endLine || undefined,
                )
                .join("\n");

              return (
                "<code-change-json>\n" +
                JSON.stringify(item.aiCodeChange, null, 2) +
                "</code-change-json>"
              );
            }

            return item.content;
          })
          .join("\n");

        const updateMessage = async () => {
          setUpdatedMessage(true);

          const updatedMessage: any = {
            ...message,
            content: newContents,
          };

          useSupabaseStore.getState().onDbSnapshot("aiChatMessages", {
            newItems: [updatedMessage],
          });

          await supabaseClient
            .from("ai_chat_messages")
            .update({
              content: newContents,
            })
            .eq("id", message.id);
        };

        if (filesToReload?.length) {
          useCodebaseStore.getState().setReloadFiles({ files: filesToReload });
        } else if (needToChange) {
          updateMessage();
        }
      } catch (e) {
        console.error(e);
      }
    }, [
      contentArray,
      message.message_context,
      fileToContentsFromApi,
      supabaseClient,
      updatedMessage,
      message,
    ]);

    return (
      <div
        className={cn(
          "group relative mb-2 flex flex-col w-full pr-2",
          hasSearchMatch &&
            "border-l-2 border-primary-500 pl-2 rounded bg-primary-50 dark:bg-primary-n-900/10",
          message.role === "user" && !hasDevErrors && "items-end",
        )}
      >
        {/* Message content - wrapped for opacity control */}
        <div
          className={cn(
            "flex-1 flex flex-col gap-0.5 overflow-hidden",
            message.role === "user" && !hasDevErrors && "max-w-[80%]",
            isGreyedOut && "grayscale",
          )}
        >
          <div
            className={cn(
              "flex flex-col gap-0.5 overflow-hidden",
              message.role === "user" && !hasDevErrors
                ? "bg-[#2F3035] rounded-lg px-2 pt-2 pb-[4px]"
                : "",
            )}
          >
            {/* Message context (images, selected element) - inside user message bubble */}
            {message.role === "user" &&
              ((message?.message_context?.imageUrls?.length ?? 0) > 0 ||
                (message?.message_context?.files?.length ?? 0) > 0 ||
                (message?.message_context?.totalUserSelection?.length ?? 0) >
                  0) && (
                <div className="flex flex-col gap-2 mb-2">
                  {message?.message_context?.imageUrls?.map((url, index) => (
                    <FilePreviewItem
                      key={index}
                      file={{
                        url,
                        name: url.split("/").pop() || url,
                        type: "image",
                        mimeType: "image/png",
                      }}
                    />
                  ))}
                  {message?.message_context?.files?.map((file, index) => (
                    <FilePreviewItem key={index} file={file} />
                  ))}
                  {message?.message_context?.totalUserSelection?.map(
                    (selectedElement, index) => (
                      <ContextButton
                        key={index}
                        treeElementOrStoryboard={selectedElement}
                      />
                    ),
                  )}
                </div>
              )}

            {contentArray.map((block, index) => {
              if (block.type === "figmaInput") {
                return <FigmaBlock />;
              }

              if (block.type === "reasoning") {
                return (
                  <ReasoningChatMessage
                    block={block}
                    allSegments={contentArray}
                  />
                );
              }

              if (block.type === "reasoning-timing") {
                // Skip rendering - this data is consumed by ReasoningChatMessage
                return null;
              }

              if (block.type === "search-grounding-sources") {
                return <SearchGroundingSourcesChatMessage block={block} />;
              }

              if (block.toolCall) {
                const status = block.toolCallStatus || "in-progress";
                const toolName = block.toolCallName;
                const toolCallId = block.toolCallId;
                const toolResult = block.toolCallResult;

                // Parse the args from the content (which is now JSON stringified args)
                let args = {};
                try {
                  args = JSON.parse(block.content || "{}");
                } catch (e) {
                  console.warn(
                    "Failed to parse tool call args:",
                    block.content,
                  );
                }

                return (
                  <ToolCallBlock
                    key={index}
                    toolName={toolName || "unknown"}
                    args={args}
                    status={status}
                    toolResult={toolResult}
                    toolCallId={toolCallId}
                    messageId={message.id}
                    isLastMessage={isLastMessage}
                    isChatLoading={isChatLoading}
                    onAnswerQuestions={submitAnswers}
                    isSubmittingQuestions={isSubmittingAnswers}
                    pendingQuestionsToolCallId={pendingQuestions?.toolCallId}
                  />
                );
              }

              if (
                block.type === "codeSnippet" ||
                block.type === "aiCodeChange" ||
                block.type === "aiChanges"
              ) {
                return (
                  <EngSpecBlock
                    key={index}
                    block={block}
                    message={message}
                    blockIndex={index}
                    loadingAiChange={
                      isLastMessage &&
                      index === contentArray.length - 1 &&
                      isChatLoading
                    }
                  />
                );
              }

              if (block.type === "prd-json") {
                return (
                  <PRDBlock
                    key={index}
                    block={block}
                    message={message}
                    blockIndex={index}
                    isLastBlock={index === contentArray.length - 1}
                  />
                );
              }

              if (block.type === "vercel-deployment-message") {
                return (
                  <VercelDeploymentBlock
                    previewUrl={block?.parsedJson?.items?.previewUrl}
                    claimUrl={block?.parsedJson?.items?.claimUrl}
                  />
                );
              }

              if (block.type === "action-progress") {
                // Determine if this progress block should be grouped
                let groupTitle: string | undefined;
                if (
                  message?.content?.includes(
                    "Creating wireframes based on the PRD and mermaid diagram",
                  )
                ) {
                  groupTitle = "Generating app";
                }
                // Can add more conditions here for other groupings in the future
                // e.g., if (message?.content?.includes("Setting up Supabase")) groupTitle = "Database setup";

                return (
                  <ProgressBlock
                    key={index}
                    progressItems={block.parsedJson?.items || []}
                    groupTitle={groupTitle}
                  />
                );
              }

              if (block.type === "dev-errors") {
                return (
                  <ErrorsBlock
                    key={index}
                    errors={block.parsedJson || []}
                    isLastMessage={isLastMessage}
                  />
                );
              }

              if (block.type === "env-variables-input") {
                return (
                  <EnvVariablesInputBlock
                    key={index}
                    title={
                      block.parsedJson?.title || "Set Up Your Env Variables"
                    }
                    variables={block.parsedJson?.variables || []}
                    message={message}
                  />
                );
              }

              // Skip rendering code-edit-stream blocks (they're still counted for tokens)
              if (block.type === "code-edit-stream") {
                return null;
              }

              return (
                <SearchQueryProvider searchQuery={searchQuery}>
                  {/* @ts-expect-error TS2745 */}
                  <ConfiguredReactMarkdown
                    key={index}
                    className="text-xs my-0.5"
                  >
                    {block.content}
                  </ConfiguredReactMarkdown>
                </SearchQueryProvider>
              );
            })}
          </div>
        </div>
        {/* Usage Indicator and Restore Button for assistant messages - outside opacity wrapper */}
        {/* Only applicable for Max Agent. The component wont be rendered for the classic agent since the metrics are NOT returned on the aiChat endpoint */}
        {message.role === "assistant" &&
          !isGreyedOut &&
          (() => {
            const hasUsageMetrics =
              (message.message_context?.promptsConsumed ?? 0) > 0 ||
              (message.message_context?.toolCallsCount ?? 0) > 0 ||
              (message.message_context?.inputTokens ?? 0) > 0 ||
              (message.message_context?.outputTokens ?? 0) > 0;

            const { latestActivity, isCurrentHead, isOverwritten } =
              getRestoreState(message.id);
            const hasRestoreButton =
              !isChatLoading &&
              !isCurrentHead &&
              (latestActivity || isOverwritten);

            // Only render container if there's content to show
            if (!hasUsageMetrics && !hasRestoreButton) {
              return null;
            }

            return (
              <div className="mt-2 mb-2 flex items-center gap-2 border border-secondary-n-800/50 rounded-md px-2 py-1 w-fit">
                <ChatMessageUsageIndicator
                  promptsConsumed={message.message_context?.promptsConsumed}
                  toolCallsCount={message.message_context?.toolCallsCount}
                  inputTokens={message.message_context?.inputTokens}
                  outputTokens={message.message_context?.outputTokens}
                />
                {hasUsageMetrics && hasRestoreButton && (
                  <div className="h-1.5 w-px bg-secondary-n-700" />
                )}
                <MessageRestoreButton message={message} />
              </div>
            );
          })()}
      </div>
    );
  },
);
